import { z } from "zod";
export const TeamId = z.string().min(1).max(160);
export const TeamModelSource = z.enum(["member", "publisher"]);
export const TeamModelPin = z
  .object({
    credentialId: TeamId,
    provider: z.string().min(1).max(200),
    modelId: z.string().min(1).max(300),
    thinkingLevel: z.string().nullable().default(null),
  })
  .strict();
export type TeamModelPinValue = z.infer<typeof TeamModelPin>;
export const TeamBotConfig = z.object({
  instructions: z.string(),
  color: z.string(),
  disabledBuiltinTools: z.array(z.string()),
  modelSource: TeamModelSource.default("member"),
  modelPin: TeamModelPin.nullable().default(null),
});
export const TeamRole = z.enum(["owner", "admin", "member"]);
export const TeamMember = z.object({
  userId: TeamId,
  name: z.string(),
  email: z.string().nullable(),
  role: TeamRole,
});
export const TeamInvite = z.object({
  id: TeamId,
  email: z.string(),
  role: z.enum(["admin", "member"]),
  expiresAt: z.string(),
  status: z.string(),
});
export const TeamSummary = z.object({
  spaceId: TeamId,
  name: z.string(),
  userId: TeamId,
  role: TeamRole,
  members: z.array(TeamMember),
  invitations: z.array(TeamInvite),
});
export const TeamInviteInput = z
  .object({
    email: z
      .string()
      .trim()
      .email()
      .max(254)
      .transform((v) => v.toLowerCase()),
    role: z.enum(["admin", "member"]).default("member"),
  })
  .strict();
export const TeamInviteToken = z
  .object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/) })
  .strict();
export const TeamInvitePreview = z.object({
  email: z.string(),
  team: z.string(),
  inviter: z.string(),
  expiresAt: z.string(),
});
export const SharedTeamBot = z.object({
  id: TeamId,
  name: z.string(),
  description: z.string(),
  ownerName: z.string(),
  owned: z.boolean(),
  revision: z.number().int(),
  enabled: z.boolean(),
  sourceBotId: TeamId.nullable(),
  modelSource: TeamModelSource,
  modelProvider: z.string().nullable(),
  modelId: z.string().nullable(),
});
export const TeamBotPublishInput = z
  .object({
    botId: TeamId,
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(1000).default(""),
    modelSource: TeamModelSource.default("member"),
  })
  .strict();
export const TeamChannel = z.object({
  id: TeamId,
  name: z.string(),
  topic: z.string(),
  kind: z.enum(["channel", "direct"]),
  canManage: z.boolean(),
  unread: z.number().int(),
  members: z.array(TeamMember),
  agents: z.array(SharedTeamBot),
});
export const TeamChannelCreateInput = z
  .object({
    name: z.string().trim().min(1).max(80),
    topic: z.string().trim().max(500).default(""),
    userIds: z.array(TeamId).max(200).default([]),
    publicationIds: z.array(TeamId).max(20).default([]),
  })
  .strict();
export const TeamChannelMembersInput = z
  .object({
    channelId: TeamId,
    userIds: z.array(TeamId).max(200),
    publicationIds: z.array(TeamId).max(20),
  })
  .strict();
export const TeamChannelMessage = z.object({
  id: TeamId,
  seq: z.number().int(),
  authorName: z.string(),
  own: z.boolean(),
  kind: z.enum(["user", "agent", "status"]),
  content: z.string(),
  replyToId: z.string().nullable(),
  createdAt: z.string(),
  editedAt: z.string().nullable(),
  runId: z.string().nullable(),
  privateBotId: z.string().nullable(),
  runStatus: z.string().nullable(),
});
export const TeamChannelMessagesInput = z
  .object({ channelId: TeamId, before: z.number().int().positive().optional() })
  .strict();
export const TeamChannelMessages = z.object({
  messages: z.array(TeamChannelMessage),
  older: z.number().int().nullable(),
  newest: z.number().int(),
});
export const TeamChannelPostInput = z
  .object({
    channelId: TeamId,
    content: z.string().trim().min(1).max(20000),
    replyToId: TeamId.optional(),
    nonce: z.string().min(1).max(100),
    agentIds: z.array(TeamId).max(5).default([]),
  })
  .strict();
export type SharedTeamBotView = z.infer<typeof SharedTeamBot>;
export type TeamMemberView = z.infer<typeof TeamMember>;
